import {
  Exchange,
  Operation,
  OperationResult,
  CombinedError
} from "@urql/core";
import { Logger } from "monitoring";
import {
  filter,
  fromPromise,
  fromValue,
  map,
  mergeMap,
  pipe,
  Source,
  subscribe,
  tap,
  merge
} from "wonka";
import { DocumentNode, Kind, OperationDefinitionNode } from "graphql";
import ReactGA from "react-ga";
import { isString } from "lodash";

/**
 * Creating an instance of CombinedError will invoke a heavy string operation to create the message.
 * Lets use a type for the "in the middle" as we generate a smaller error stack for logging to DD.
 */
export type TheErrorRoots = Pick<
  CombinedError,
  "networkError" | "response" | "graphQLErrors"
>;
export const getOperationName = (query: DocumentNode): string | null => {
  const node = query.definitions.find(
    (node: any): node is OperationDefinitionNode => {
      return node.kind === Kind.OPERATION_DEFINITION && node.name;
    }
  );
  return node !== undefined && node.name ? node.name.value : null;
};

/**
 * The type on combined error is a liar, you can get a string for a network error.
 * @param nError
 */
export function parseNetworkError(nError: string | Error | undefined): string {
  if (!nError) {
    return "";
  }
  if (isString(nError)) {
    return nError;
  }
  return nError?.message ?? "";
}

/**
 * errorLoggerExchange listens to the operations and logs any errors that happened to the logger.
 * https://formidable.com/open-source/urql/docs/basics/errors/
 *
 * Generally, this could be replaced by the errorExchange from import { errorExchange } from "urql";
 * but our service errors has some particular qualities that made that tougher, for example we could get
 * non graphql style, non-network errors, which did not fit into combined errors.
 *  Additionally, our errors could be too long, and rejected silently by data dog logger, so we trim them here as well.
 * @param logger
 */
export const errorLoggerExchange: (l: Logger) => Exchange = logger => {
  const logError = (op: Operation, error: CombinedError) => {
    const { response, networkError, graphQLErrors } = error;
    const opName = getOperationName(op.query) || "Unknown Operation";
    /**
     * Build a pipeline that converts the CombinedError to a simpler TheErrorRoots with basic props.
     */
    const responseMessage = (): Source<TheErrorRoots> => {
      const resp = response as Response;

      const respError = (e: any) =>
        logger.error("Failed to even read the graphql error response", {
          error: e,
          operation: opName
        });

      if (!resp) {
        logger.info("No response on the request", { operation: opName });
        return fromValue(error);
      }

      const { status, statusText } = resp;
      if (status === 401) {
        return fromValue(error);
      }

      const readBody = async (r: Response) => {
        try {
          return (await r.text()) || "";
        } catch (e) {
          respError(e);
          return "";
        }
      };

      /**
       * Build a pipeline that could map the error to TheErrorRoots if it was in the http body and not a normal graphql error
       * nor an typical 401 auth error.
       */
      const fromHttpBody = pipe(
        fromValue(resp),
        filter(() => !graphQLErrors || graphQLErrors.length === 0),
        mergeMap(r => fromPromise(readBody(r))),
        map(body => {
          try {
            if (
              body.length > 0 &&
              resp.headers.get("content-type") === "application/json"
            ) {
              return JSON.parse(body);
            }
          } catch (e) {
            respError(e);
            return body;
          }
          return body;
        }),
        map(body => ({
          response: { status, statusText, body },
          networkError: networkError,
          graphQLErrors: graphQLErrors
        }))
      );

      /**
       * Mad the Combined error into a simpler TheErrorRoots
       */
      const fromGraphqlErrors = pipe(
        fromValue(graphQLErrors),
        filter(() => graphQLErrors && graphQLErrors.length !== 0),
        map(() => ({
          response: { status, statusText, body: "" },
          networkError: networkError,
          graphQLErrors: graphQLErrors
        }))
      );

      //Combine our two error methods into one pipeline.
      return merge([fromHttpBody, fromGraphqlErrors]);
    };

    //Log the TheErrorRoots if it exists to Google Analytics and Data Dog
    const logCombinedError = ({
      response,
      networkError,
      graphQLErrors
    }: TheErrorRoots) => {
      const logError = {
        graphQLErrors: graphQLErrors
          .slice(0, 10)
          .map(
            gE => gE.message || gE.extensions?.code || gE.originalError?.message
          ),
        response,
        networkError: parseNetworkError(networkError)
      };

      logger.error(`Failed Graphql ${op.kind}`, {
        operation: opName,
        error: logError
      });

      ReactGA.exception({
        description: `Failed Graphql ${op.kind}`,
        fatal: false
      });
    };

    pipe(
      responseMessage(),
      filter(resp => resp?.response?.status !== 401),
      subscribe(logCombinedError)
    );
  };

  const logOperationResult = (result: OperationResult) => {
    const { error, operation } = result;
    if (!error) return;
    return logError(operation, error);
  };

  return ({ forward }) => {
    return ops$ => pipe(ops$, forward, tap(logOperationResult));
  };
};
